// AuthContext.js
import React, { createContext, useContext, useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

import { login, logout, verifyUser, setSessionExpiredHandler } from "../utils/api";
import { io } from "socket.io-client";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

const getSocketServerUrl = () => {
  if (process.env.REACT_APP_SOCKET_URL) {
    return process.env.REACT_APP_SOCKET_URL;
  }

  try {
    return new URL(API_URL).origin;
  } catch {
    return API_URL.replace(/\/api\/?$/, "");
  }
};

const AuthContext = createContext({
  user: null,
  loading: true,
  handleLogin: () => null,
  handleLogout: () => null,
  completeLogin: () => null,
  refreshUser: () => null,
  socket: null,
});

export const AuthProvider = ({ children }) => {
  const [auth, setAuth] = useState({
    loading: true,
    user: null,
  });
  const navigate = useNavigate();
  const [socket, setSocket] = useState(null);
  const socketRef = useRef(null);
  const userRef = useRef(null);
  const handlingExpireRef = useRef(false);
  const loggingOutRef = useRef(false);

  const connectSocket = () => {
    if (socketRef.current) {
      socketRef.current.disconnect();
    }

    // Auth cookie is sent automatically with withCredentials.
    const websocket = io.connect(getSocketServerUrl(), {
      path: "/socket.io/",
      transports: ["websocket"],
      withCredentials: true,
    });

    socketRef.current = websocket;
    setSocket(websocket);
    return websocket;
  };

  const disconnectSocket = () => {
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
      setSocket(null);
    }
  };

  const connectSocketDeferred = () => {
    // Defer websocket until after first paint so auth/UI aren't blocked.
    const schedule =
      typeof window !== "undefined" && window.requestIdleCallback
        ? (cb) => window.requestIdleCallback(cb, { timeout: 1500 })
        : (cb) => setTimeout(cb, 0);
    schedule(() => {
      if (!socketRef.current) {
        connectSocket();
      }
    });
  };

  const checkUser = async () => {
    try {
      const { data } = await verifyUser();
      setAuth({
        user: data,
        loading: false,
      });
      connectSocketDeferred();
    } catch (err) {
      setAuth({
        user: null,
        loading: false,
      });
      disconnectSocket();
    }
  };

  const handleLogin = async (email, password) => {
    try {
      const { data } = await login(email, password);

      // If 2FA is required, redirect to verification page
      if (data.requires2FA) {
        navigate("/verify-2fa", {
          state: { tempToken: data.tempToken },
        });
        return;
      }

      // Session JWT is in an httpOnly cookie set by the server.
      setAuth({
        user: data.user,
        loading: false,
      });
      connectSocketDeferred();
      navigate(data.user?.two_fa_enabled ? "/" : "/setup-2fa");
    } catch (error) {
      toast.error(
        error.response?.data?.message ??
          "Login failed. Please check your credentials.",
        {
          position: "top-right",
          autoClose: 3000,
        }
      );
    }
  };

  // Called after successful 2FA verification (cookie already set by server)
  const completeLogin = (user) => {
    setAuth({
      user,
      loading: false,
    });
    connectSocketDeferred();
  };

  // Refresh user data from the server (e.g. after enabling/disabling 2FA)
  const refreshUser = async () => {
    try {
      const { data } = await verifyUser();
      setAuth((prev) => ({
        ...prev,
        user: data,
      }));
      return data;
    } catch (err) {
      console.error("Failed to refresh user:", err);
      return null;
    }
  };

  useEffect(() => {
    userRef.current = auth.user;
  }, [auth.user]);

  useEffect(() => {
    checkUser();
    return () => {
      disconnectSocket();
    };
  }, []);

  useEffect(() => {
    setSessionExpiredHandler(() => {
      if (
        !userRef.current ||
        handlingExpireRef.current ||
        loggingOutRef.current
      ) {
        return;
      }
      handlingExpireRef.current = true;
      toast.error("Session expired. Please log in.", {
        position: "top-right",
        autoClose: 4000,
      });
      logout().catch(() => {});
      setAuth({
        user: null,
        loading: false,
      });
      disconnectSocket();
      navigate("/login");
      handlingExpireRef.current = false;
    });
    return () => setSessionExpiredHandler(null);
  }, [navigate]);

  const handleLogout = () => {
    if (loggingOutRef.current) {
      return;
    }
    loggingOutRef.current = true;
    userRef.current = null;
    setAuth({
      user: null,
      loading: false,
    });
    disconnectSocket();
    navigate("/login", { replace: true });
    logout()
      .catch(() => {})
      .finally(() => {
        loggingOutRef.current = false;
      });
  };

  return (
    <AuthContext.Provider
      value={{ ...auth, socket, handleLogin, handleLogout, completeLogin, refreshUser }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
